import {
  BottomSheet,
  LoadingSpinner,
  Text,
  Title,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { FormControlLabel, Radio, RadioGroup, Stack } from "@mui/material";
import { useToast } from "@src/appV2/lib";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useQueryClient } from "@tanstack/react-query";

import { IconButton } from "../../../components/IconButton";
import ErrorComponent from "../../components/ErrorComponent";
import {
  getWorkerFriendSettingsUrl,
  ShiftVisibilityEnum,
  useGetWorkerFriendSettings,
} from "../../hooks/useGetWorkerFriendSettings";
import { useSetShiftVisibility } from "../../hooks/useSetShiftVisibility";

const visibilityOptions = [
  {
    value: ShiftVisibilityEnum.PUBLIC,
    label: "Visible to everyone",
    description: "Anyone can see when you're working on the same shift",
  },
  {
    value: ShiftVisibilityEnum.FRIENDS,
    label: "Visible to friends only",
    description: "Only your friends can see when you're working on the same shift",
  },
] as const;

type VisibilityOption = (typeof visibilityOptions)[number]["value"];

interface Props {
  modalState: UseModalState;
  onClose: () => void;
}

export function VisibilitySettingsBottomSheet(props: Props) {
  const { modalState, onClose } = props;
  const queryClient = useQueryClient();
  const worker = useDefinedWorker();
  const { showErrorToast } = useToast();

  const {
    data: friendSettings,
    isLoading: isLoadingFriendSettings,
    isRefetching: isRefetchingFriendSettings,
    isError,
  } = useGetWorkerFriendSettings();

  const { mutate: setShiftVisibility, isLoading: isSettingShiftVisibility } = useSetShiftVisibility(
    {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: [getWorkerFriendSettingsUrl(worker.userId)],
        });
      },
      onError: () => {
        showErrorToast("Failed to set visibility. Please try again.");
      },
    }
  );

  function handleVisibilityChange(event: React.ChangeEvent<HTMLInputElement>) {
    setShiftVisibility(event.target.value as VisibilityOption);
  }

  const isLoading =
    isLoadingFriendSettings || isSettingShiftVisibility || isRefetchingFriendSettings;

  return (
    <BottomSheet modalState={modalState}>
      <Stack flex={1} flexDirection="column" p={6}>
        <Stack flex={1} alignItems="flex-end" p={4} mb={6}>
          <IconButton
            variant="outlined"
            iconType="close"
            size="small"
            aria-label="close"
            onClick={() => {
              onClose();
            }}
          />
        </Stack>
        <Stack px={6} pb={10}>
          <Title component="h2" variant="h3" color="text.primary" pb={8}>
            Who can see you booked into shifts in the app
          </Title>
          {isError && (
            <Stack flex={1} justifyContent="center" alignItems="center">
              <ErrorComponent />
            </Stack>
          )}
          {!isError && (isLoading || !isDefined(friendSettings)) && (
            <Stack flex={1} justifyContent="center" alignItems="center">
              <LoadingSpinner size="medium" />
            </Stack>
          )}
          {!isError && !isLoading && isDefined(friendSettings) && (
            <RadioGroup
              value={friendSettings.data.attributes.shiftVisibility}
              onChange={async (event) => {
                handleVisibilityChange(event);
              }}
            >
              <Stack spacing={4}>
                {visibilityOptions.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio />}
                    label={
                      <Stack ml={1}>
                        <Text variant="body1" color="text.primary" sx={{ fontWeight: "bold" }}>
                          {option.label}
                        </Text>
                        <Text variant="body2" color="text.secondary">
                          {option.description}
                        </Text>
                      </Stack>
                    }
                  />
                ))}
              </Stack>
            </RadioGroup>
          )}
        </Stack>
      </Stack>
    </BottomSheet>
  );
}
